import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Chip,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useAccountCatalogSyncService } from '../../services/accountCatalogSyncService';
import { useAccountService } from '../../services/accountService';
import type { AccountCatalogSyncConfigDto, AccountCatalogSyncRunDto, SpringPage } from '../../types/accountCatalogSync';

const CRON_DOC_HREF =
  'https://docs.spring.io/spring-framework/docs/current/javadoc-api/org/springframework/scheduling/support/CronTrigger.html';

const formatTs = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const triggerTypeLabel = (t: AccountCatalogSyncRunDto['triggerType']) =>
  t === 'SCHEDULED' ? 'Programada' : 'Manual';

const AccountCatalogSyncSettingsTab: React.FC = () => {
  const { jwtToken } = useAuth();
  const accountCatalogSyncService = useAccountCatalogSyncService();
  const { syncAccounts } = useAccountService();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [config, setConfig] = useState<AccountCatalogSyncConfigDto | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [cronExpression, setCronExpression] = useState('0 0 2 * * ?');
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [runsPage, setRunsPage] = useState<SpringPage<AccountCatalogSyncRunDto> | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!jwtToken) {
      setGlobalError('No hay token de autenticación');
      setLoading(false);
      return;
    }
    setLoading(true);
    setGlobalError(null);
    const res = await accountCatalogSyncService.getCronConfig();
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        setGlobalError('No tiene permiso para ver esta configuración (se requiere rol ADMIN en el backend).');
      } else {
        setGlobalError(res.error || 'No se pudo cargar la configuración');
      }
      setConfig(null);
    } else {
      setConfig(res.data);
      setEnabled(res.data.enabled);
      setCronExpression(res.data.cronExpression);
    }
    setLoading(false);
  }, [jwtToken]);

  const loadRuns = useCallback(async () => {
    if (!jwtToken) return;
    setRunsLoading(true);
    setRunsError(null);
    const res = await accountCatalogSyncService.getRuns(page, rowsPerPage);
    if (!res.ok) {
      setRunsError(res.error || 'No se pudo cargar el historial');
      setRunsPage(null);
    } else {
      setRunsPage(res.data);
    }
    setRunsLoading(false);
  }, [jwtToken, page, rowsPerPage]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!loading && config) void loadRuns();
  }, [loading, config, loadRuns]);

  const handleSave = async () => {
    if (!jwtToken) return;
    setSaving(true);
    setFieldErrors({});
    setGlobalError(null);
    setSuccessMessage(null);
    const res = await accountCatalogSyncService.updateCronConfig({
      enabled,
      cronExpression: cronExpression.trim()
    });
    if (!res.ok) {
      if (res.fieldErrors) setFieldErrors(res.fieldErrors);
      else setGlobalError(res.error || 'Error al guardar');
    } else {
      setConfig(res.data);
      setEnabled(res.data.enabled);
      setCronExpression(res.data.cronExpression);
      setSuccessMessage('Configuración guardada. El backend reprogramará la sincronización con el nuevo cron.');
      void loadRuns();
    }
    setSaving(false);
  };

  const handleConfirmRun = async () => {
    setConfirmRunOpen(false);
    setRunning(true);
    setGlobalError(null);
    setSuccessMessage(null);
    try {
      const result = await syncAccounts();
      const msg =
        typeof result?.message === 'string'
          ? result.message
          : 'Sincronización completada.';
      setSuccessMessage(msg);
      setPage(0);
      if (jwtToken) {
        setRunsLoading(true);
        const runsRes = await accountCatalogSyncService.getRuns(0, rowsPerPage);
        if (!runsRes.ok) {
          setRunsError(runsRes.error || 'No se pudo actualizar el historial');
        } else {
          setRunsPage(runsRes.data);
        }
        setRunsLoading(false);
      }
    } catch (e) {
      setGlobalError(e instanceof Error ? e.message : 'Error al sincronizar el catálogo de cuentas');
    } finally {
      setRunning(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {globalError && (
        <Alert severity="error" onClose={() => setGlobalError(null)}>
          {globalError}
        </Alert>
      )}
      {runsError && (
        <Alert severity="warning" onClose={() => setRunsError(null)}>
          {runsError}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Alert severity="info">
        La ubicación ComPsych y las credenciales las configura el equipo de plataforma en el servidor. Esta pantalla
        solo define cuándo se ejecuta la sincronización programada del catálogo de cuentas (ComPsych → tabla{' '}
        <code>accounts</code>). Zona horaria del cron en servidor: <strong>America/Mexico_City</strong>.
      </Alert>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ScheduleIcon sx={{ mr: 1 }} />
            Sincronización programada
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La configuración se guarda en <code>account_catalog_sync_config</code>. Al guardar, el backend reprograma
            el job con el nuevo cron y el estado activo/inactivo.
          </Typography>
          {config && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Última actualización: {formatTs(config.updatedAt ?? undefined)} —{' '}
              {config.updatedByUsername ?? '—'}
            </Typography>
          )}
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(_, v) => setEnabled(v)} color="primary" />}
              label="Activar sincronización programada"
            />
            <TextField
              label="Expresión cron (Spring, 6 campos)"
              value={cronExpression}
              onChange={(e) => setCronExpression(e.target.value)}
              fullWidth
              required
              disabled={!config}
              error={Boolean(fieldErrors.cronExpression)}
              helperText={
                fieldErrors.cronExpression ? (
                  fieldErrors.cronExpression
                ) : (
                  <span>
                    Formato: segundo, minuto, hora, día del mes, mes, día de semana. Ej.: todos los días a las 02:00 →{' '}
                    <code>0 0 2 * * ?</code>; a la 01:30 → <code>0 30 1 * * ?</code>.{' '}
                    <Link href={CRON_DOC_HREF} target="_blank" rel="noopener noreferrer">
                      Documentación CronTrigger (Spring)
                    </Link>
                  </span>
                )
              }
            />
            {fieldErrors.enabled && <FormHelperText error>{fieldErrors.enabled}</FormHelperText>}
            <Box>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={() => void handleSave()}
                disabled={saving || !config}
              >
                Guardar
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SyncIcon sx={{ mr: 1 }} />
            Sincronización manual
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ejecuta la misma lógica que el cron (<code>POST /api/accounts/sync</code>). Requiere rol ADMIN, SUPERVISOR
            o COMPSYCH_SYNC. La ejecución queda registrada en el historial.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={running ? <CircularProgress size={18} /> : <PlayArrowIcon />}
            onClick={() => setConfirmRunOpen(true)}
            disabled={running}
          >
            Sincronizar catálogo ahora
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1 }} />
              Historial de ejecuciones
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={runsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => void loadRuns()}
              disabled={runsLoading || !config}
            >
              Actualizar
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Fin</TableCell>
                  <TableCell>Ubicación</TableCell>
                  <TableCell align="right">Creadas</TableCell>
                  <TableCell align="right">Actualizadas</TableCell>
                  <TableCell align="right">Errores</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Disparador</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runsLoading && !runsPage?.content?.length ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : runsPage?.content?.length ? (
                  runsPage.content.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatTs(row.startedAt)}</TableCell>
                      <TableCell>{formatTs(row.finishedAt ?? undefined)}</TableCell>
                      <TableCell>{row.locationNumber}</TableCell>
                      <TableCell align="right">{row.accountsCreated}</TableCell>
                      <TableCell align="right">{row.accountsUpdated}</TableCell>
                      <TableCell align="right">{row.accountsErrors}</TableCell>
                      <TableCell align="right">{row.accountsTotal}</TableCell>
                      <TableCell>
                        <Chip size="small" label={triggerTypeLabel(row.triggerType)} variant="outlined" />
                      </TableCell>
                      <TableCell>{row.triggeredByUsername ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status === 'SUCCESS' ? 'Éxito' : 'Fallido'}
                          color={row.status === 'SUCCESS' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.errorMessage ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No hay ejecuciones registradas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {runsPage && (
            <TablePagination
              component="div"
              count={runsPage.totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmRunOpen} onClose={() => setConfirmRunOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar sincronización manual</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se iniciará la sincronización del catálogo de cuentas con ComPsych. Puede tardar varios segundos según el
            volumen de datos. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRunOpen(false)}>Cancelar</Button>
          <Button onClick={() => void handleConfirmRun()} color="primary" variant="contained" autoFocus>
            Sincronizar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AccountCatalogSyncSettingsTab;
